import { AlertTriangle } from 'lucide-react';
import type { OrigemPraticado, PublicacaoSnapshot, PublicacaoSnapshotFalha, PublicacaoSnapshotPreco } from '@triade/contracts';
import { usePublicacaoDetalhe } from '@/api/publicacoes';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { formatarDataHora, formatarCusto, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { MensagemPublicacoes, SeloSituacao, vigenciaTexto } from './publicacoes-util';

const ROTULO_ORIGEM: Record<OrigemPraticado, string> = { BASE: 'Base', DERIVADO: 'Derivado', MANUAL: 'Manual', DIRETO: 'Direto' };

export interface PublicacaoSnapshotDialogProps {
  /** Publicacao aberta; null = fechado. */
  id: string | null;
  onOpenChange: (aberto: boolean) => void;
}

/** Snapshot gravado da publicacao: so leitura. Custo, sugerido e lucro aparecem so se a API os devolveu (permissao custo.ver). */
export function PublicacaoSnapshotDialog({ id, onOpenChange }: PublicacaoSnapshotDialogProps) {
  const detalhe = usePublicacaoDetalhe(id);
  const p = detalhe.data;
  const snap = p?.snapshot ?? null;

  return (
    <Dialog open={id !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Publicação</DialogTitle>
          {p && (
            <DialogDescription asChild>
              <div className="flex flex-col gap-1 text-small text-ink-muted">
                <div className="flex flex-wrap items-center gap-2">
                  <SeloSituacao situacao={p.situacao} />
                  <span className="tabular text-ink">{vigenciaTexto(p)}</span>
                </div>
                <span>
                  Publicada por {p.publicadoPorNome ?? '—'} em {formatarDataHora(p.publicadaEm)}
                </span>
                {p.observacao && <span className="text-ink-body">Observação: {p.observacao}</span>}
              </div>
            </DialogDescription>
          )}
        </DialogHeader>

        {detalhe.isPending && <MensagemPublicacoes papel="status">Carregando publicação…</MensagemPublicacoes>}
        {detalhe.isError && <ErroFormacao erro={detalhe.error} titulo="Não foi possível abrir a publicação" />}
        {p && !snap && <MensagemPublicacoes>Esta publicação não tem o snapshot gravado.</MensagemPublicacoes>}
        {p && snap && (
          <div className="flex flex-col gap-6">
            {snap.semCusto?.length ? <AvisoSemCusto falhas={snap.semCusto} /> : null}
            <Precos precos={snap.precos} />
            <Escopo liberados={snap.liberados} naoLiberados={snap.naoLiberados} />
            {snap.parametros && <Parametros parametros={snap.parametros} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Publicacao gravada com preco mas sem custo/sugerido/lucro (o motor nao calculou). Lista recolhivel; sem o campo, nada aparece. */
function AvisoSemCusto({ falhas }: { falhas: PublicacaoSnapshotFalha[] }) {
  const itens = new Set(falhas.map((f) => f.itemId)).size;
  return (
    <section aria-label="Publicada sem custo" className="rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-3">
      <div className="flex items-center gap-2 font-semibold">
        <AlertTriangle aria-hidden="true" className="size-[18px] shrink-0 text-warning-ink" />
        <span>Publicada sem custo em {itens} itens</span>
      </div>
      <details className="mt-2 text-small text-ink-secondary">
        <summary className="cursor-pointer select-none font-medium text-ink">Ver itens ({falhas.length})</summary>
        <ul className="mt-2 flex flex-col gap-1.5">
          {falhas.map((f, i) => (
            <li key={`${f.itemId}-${f.tabelaCodigo}-${f.condicaoNome}-${i}`} className="rounded-lg bg-card px-3 py-2">
              <span className="font-medium text-ink">{f.itemCodigo}</span> — {f.itemDescricao}
              <span className="text-ink-muted">
                {' '}
                · {f.tabelaCodigo} · {f.condicaoNome} · <span className="tabular">{f.codigo}</span>
              </span>
              {f.mensagem && <div className="text-ink-muted">{f.mensagem}</div>}
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}

function Precos({ precos }: { precos: PublicacaoSnapshotPreco[] }) {
  const comCusto = precos.some((r) => r.custo !== undefined);
  return (
    <section aria-label="Preços publicados" className="flex flex-col gap-2">
      <h3 className="text-title font-semibold">Preços ({precos.length})</h3>
      {precos.length === 0 ? (
        <p className="text-small text-ink-muted">Nenhum preço nesta publicação.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[640px] border-collapse text-small">
            <thead>
              <tr className="bg-surface-muted text-left text-caption text-ink-muted">
                <th className="px-3 py-2 font-medium">Item</th>
                <th className="px-3 py-2 font-medium">Tabela</th>
                <th className="px-3 py-2 font-medium">Condição</th>
                <th className="px-3 py-2 text-right font-medium">Preço</th>
                <th className="px-3 py-2 font-medium">Origem</th>
                {comCusto && <th className="px-3 py-2 text-right font-medium">Custo</th>}
                {comCusto && <th className="px-3 py-2 text-right font-medium">Sugerido</th>}
                {comCusto && <th className="px-3 py-2 text-right font-medium">Lucro</th>}
              </tr>
            </thead>
            <tbody className="tabular">
              {precos.map((r) => (
                <tr key={`${r.itemId}-${r.tabelaId}-${r.condicaoId}`} className="border-t border-line-soft">
                  <td className="px-3 py-2 font-sans">
                    <span className="font-medium">{r.itemCodigo}</span> <span className="text-ink-muted">{r.itemDescricao}</span>
                  </td>
                  <td className="px-3 py-2 font-sans">{r.tabelaCodigo}</td>
                  <td className="px-3 py-2 font-sans">{r.condicaoNome}</td>
                  <td className="px-3 py-2 text-right font-semibold">{formatarMoeda(r.preco)}</td>
                  <td className="px-3 py-2 font-sans">{ROTULO_ORIGEM[r.origem]}</td>
                  {comCusto && <td className="px-3 py-2 text-right">{r.custo !== undefined ? formatarCusto(r.custo) : '—'}</td>}
                  {comCusto && <td className="px-3 py-2 text-right">{r.sugerido !== undefined ? formatarMoeda(r.sugerido) : '—'}</td>}
                  {comCusto && <td className="px-3 py-2 text-right">{r.lucro !== undefined ? formatarFracaoPercentual(r.lucro) : '—'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Escopo({ liberados, naoLiberados }: { liberados: PublicacaoSnapshot['liberados']; naoLiberados?: PublicacaoSnapshot['naoLiberados'] }) {
  // Sessao 36: publicacoes novas guardam as excecoes "Nao liberar mesmo com preco"; as antigas guardam onde o item era liberado.
  if (naoLiberados !== undefined) {
    return (
      <section aria-label="Escopo liberado" className="flex flex-col gap-2">
        <h3 className="text-title font-semibold">Escopo</h3>
        {naoLiberados.length === 0 ? (
          <p className="text-small text-ink-muted">Nenhuma exceção: todo item com preço estava liberado.</p>
        ) : (
          <>
            <p className="text-small text-ink-muted">Itens marcados como "Não liberar mesmo com preço" ({naoLiberados.length}).</p>
            <ul className="flex flex-col gap-1.5 text-small">
              {naoLiberados.map((item) => (
                <li key={item.itemId} className="rounded-lg bg-surface-muted px-3 py-2">
                  <span className="font-medium">{item.itemCodigo}</span>
                  <span className="text-ink-muted"> — não liberado em {item.onde.map((o) => `${o.tabelaCodigo} · ${o.condicaoNome}`).join('; ')}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    );
  }
  return (
    <section aria-label="Escopo liberado" className="flex flex-col gap-2">
      <h3 className="text-title font-semibold">Escopo</h3>
      {liberados.length === 0 ? (
        <p className="text-small text-ink-muted">Todos os itens liberados em todas as tabelas e condições.</p>
      ) : (
        <>
          <p className="text-small text-ink-muted">Itens liberados só em algumas combinações ({liberados.length}).</p>
          <ul className="flex flex-col gap-1.5 text-small">
            {liberados.map((item) => (
              <li key={item.itemId} className="rounded-lg bg-surface-muted px-3 py-2">
                <span className="font-medium">{item.itemCodigo}</span>
                <span className="text-ink-muted">
                  {' '}
                  — {item.onde.length === 0 ? 'não liberado em nenhuma tabela' : item.onde.map((o) => `${o.tabelaCodigo} · ${o.condicaoNome}`).join('; ')}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function Parametros({ parametros }: { parametros: NonNullable<PublicacaoSnapshot['parametros']> }) {
  return (
    <section aria-label="Parâmetros das tabelas" className="flex flex-col gap-2">
      <h3 className="text-title font-semibold">Parâmetros</h3>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[560px] border-collapse text-small">
          <thead>
            <tr className="bg-surface-muted text-left text-caption text-ink-muted">
              <th className="px-3 py-2 font-medium">Tabela</th>
              <th className="px-3 py-2 text-right font-medium">Lucro desejado</th>
              <th className="px-3 py-2 text-right font-medium">Lucro mínimo</th>
              <th className="px-3 py-2 text-right font-medium">Despesa fixa</th>
              <th className="px-3 py-2 text-right font-medium">Custo financeiro</th>
            </tr>
          </thead>
          <tbody className="tabular">
            {parametros.map((t) => (
              <tr key={t.tabelaId} className="border-t border-line-soft">
                <td className="px-3 py-2 font-sans">{t.tabelaCodigo}</td>
                <td className="px-3 py-2 text-right">{t.lucroDesejado ? formatarFracaoPercentual(t.lucroDesejado) : 'não definido'}</td>
                <td className="px-3 py-2 text-right">{formatarFracaoPercentual(t.lucroMinimo)}</td>
                <td className="px-3 py-2 text-right">{formatarFracaoPercentual(t.despesaFixa)}</td>
                <td className="px-3 py-2 text-right">{formatarFracaoPercentual(t.custoFinanceiro)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
